import React, { useRef, useLayoutEffect, useState } from 'react';
import { gsap, Linear } from 'gsap';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import ReactHtmlParser from 'react-html-parser';
import { SpacingStack } from '@components/SpacingStack';
import {
  SectionWrapperBox,
  FlexWrapper,
  TagsWrapper,
  TagsAnimation,
  Wrapper,
  ImageWrapper,
  TagImage,
  TagImageMobile,
  TagWrapper,
  TagGuestWrapper,
  TextPairingWrapper,
  TextPairingWrapperMobile,
  DesktopWrapper,
  MobileWrapper,
} from './TagSection.styles';
import { chunkArray } from '@utils/chunkArray';
import { guestListData } from '@data-ts/pages/guest-list';
import { Columns } from '@components/Columns';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

interface TagType
  extends Readonly<{
    src: string;
    srcMobile: string;
    alt: string;
  }> {}

interface TagSectionProps
  extends Readonly<{
    tags: ReadonlyArray<TagType>;
  }> {}

export const TagSection: React.FC<TagSectionProps> = ({ tags }) => {
  const tagsRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleMobile, setIsVisibleMobile] = useState(false);

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let heroTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController && tagsRef.current) {
          heroTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          heroTimeline.fromTo(
            tagsRef.current,
            {
              transform: 'translateX(-200vh)',
            },
            {
              transform: 'translateX(50vh)',
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#tags-wrapper',
          triggerHook: 'onEnter',
          duration: '200%',
        })
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            heroTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (heroTimeline) {
        heroTimeline.progress(0);
        heroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted]);

  const length = tags.length / 3;
  const inputArr = tags.map((tag, index) => (
    <ImageWrapper key={index} marginX="10px" alignItems={'center'} flexDirection={'column'} justifyContent={'center'}>
      <TagImage src={tag.src} alt={tag.alt} isMainSrcShown={isVisible} />
      <TagImageMobile src={tag.srcMobile} alt={tag.alt} isMainSrcShown={isVisibleMobile} width="fit-content" />
    </ImageWrapper>
  ));

  const columns = chunkArray(inputArr, length).map((rowContent, index) => {
    return <FlexWrapper key={index}>{rowContent}</FlexWrapper>;
  });

  const creativeTagsData = guestListData.creativeTags;

  return (
    <TagWrapper id="Tag_Guests_Any_Way_You_Want">
      <DesktopWrapper>
        <LazyLoadWrapper
          beforeLoad={() => {
            setIsVisible(true);
          }}
        >
          <div />
        </LazyLoadWrapper>
      </DesktopWrapper>
      <MobileWrapper>
        <LazyLoadWrapper
          beforeLoad={() => {
            setIsVisibleMobile(true);
          }}
        >
          <div />
        </LazyLoadWrapper>
      </MobileWrapper>
      <SectionWrapperBox
        paddingTop={[10]}
        paddingBottom={[10]}
        style={{ maxWidth: '1300px', margin: 'auto', position: 'absolute', left: 0, right: 0 }}
      >
        <TagGuestWrapper>
          <Columns>
            <Columns.Column>
              <TextPairingWrapper
                alignment="center"
                title={ReactHtmlParser(creativeTagsData.eyebrow)}
                titleMaxWidth="474px"
                titleColor="neutra6"
                titleTagName="h2"
                titleVariant="hed72Hero"
                body={creativeTagsData.content}
                bodyVariant="dek24"
                bodyColor="neutra5"
              />
              <TextPairingWrapperMobile
                alignment="center"
                title={ReactHtmlParser(creativeTagsData.eyebrow)}
                titleMaxWidth="474px"
                titleColor="white"
                titleTagName="h2"
                titleVariant="hed72Hero"
                body={creativeTagsData.content}
                bodyVariant="dek24"
                bodyColor="neutra1"
              />
            </Columns.Column>
          </Columns>
        </TagGuestWrapper>
        <Wrapper id="tags-wrapper">
          <TagsWrapper marginTop={[10, 12]} marginBottom={[11, 10]} ref={tagsRef}>
            <TagsAnimation>
              <SpacingStack spacing={[10]}>{columns}</SpacingStack>
            </TagsAnimation>
          </TagsWrapper>
        </Wrapper>
      </SectionWrapperBox>
    </TagWrapper>
  );
};
